import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper, Copy, Share2, BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatIST } from "@/components/Countdown";

const SOURCES = ["discord", "instagram", "youtube", "friend", "minecraft-server", "other"];

function GoogleButton({ onCredential }) {
  const { config } = useApp();
  const ref = useRef(null);
  const clientId = config?.google_client_id;
  useEffect(() => {
    if (!clientId || !ref.current) return;
    const init = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({ client_id: clientId, callback: (r) => onCredential(r.credential) });
      window.google.accounts.id.renderButton(ref.current, { theme: "outline", size: "large", width: 320, text: "continue_with" });
    };
    if (window.google?.accounts?.id) init();
    else {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.onload = init;
      document.body.appendChild(s);
    }
  }, [clientId, onCredential]);
  if (!clientId) {
    return (
      <div data-testid="google-login-unconfigured" className="w-full text-center text-xs text-slate-400 border border-dashed border-slate-300 rounded-lg py-3 px-4">
        Google sign-in is being configured by the admin. Use test login if enabled.
      </div>
    );
  }
  return <div data-testid="google-login-button" ref={ref} className="flex justify-center" />;
}

function ProfileForm({ onDone }) {
  const { user, setUser } = useApp();
  const [form, setForm] = useState({
    name: user?.name || "", display_name: "", phone: "", address: "",
    instagram: "", discord: "", minecraft: "", referral_source: "discord", terms_accepted: false,
  });
  const [dnStatus, setDnStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const checkDn = async (v) => {
    setForm((f) => ({ ...f, display_name: v }));
    if (v.length < 3) { setDnStatus(null); return; }
    try {
      const { data } = await api.get(`/auth/display-name/check?value=${encodeURIComponent(v)}`);
      setDnStatus(data);
    } catch { setDnStatus(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/complete-profile", form);
      localStorage.setItem("lostdc_token", data.token);
      setUser(data.user);
      onDone(data.user);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} data-testid="registration-form"
      className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-xl w-full max-w-xl space-y-5">
      <div>
        <h2 className="text-2xl font-black font-heading text-slate-900 tracking-tight">Complete Registration</h2>
        <p className="text-sm text-slate-500 mt-1">Signed in as <b>{user?.email}</b>. Your real name & email stay private — only your display name is public.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Full Name *</label>
          <Input data-testid="reg-name" className="mt-1.5 focus:ring-[#FF9933]" value={form.name} onChange={set("name")} required minLength={2} />
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Public Display Name *</label>
          <Input data-testid="reg-display-name" className="mt-1.5" value={form.display_name} onChange={(e) => checkDn(e.target.value)} required minLength={3} maxLength={24} placeholder="LostDC_Bunny" />
          {dnStatus && (
            <div data-testid="display-name-status" className={`text-xs mt-1 font-semibold ${dnStatus.available ? "text-[#138808]" : "text-red-600"}`}>
              {dnStatus.available ? "✓ Available" : `✕ ${dnStatus.reason}`}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Mobile Number *</label>
          <Input data-testid="reg-phone" className="mt-1.5" value={form.phone} onChange={set("phone")} required placeholder="+91 98XXXXXXXX" />
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Instagram Username</label>
          <Input data-testid="reg-instagram" className="mt-1.5" value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" />
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Discord Username</label>
          <Input data-testid="reg-discord" className="mt-1.5" value={form.discord} onChange={set("discord")} placeholder="username#0001" />
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Minecraft Username (optional)</label>
          <Input data-testid="reg-minecraft" className="mt-1.5" value={form.minecraft} onChange={set("minecraft")} />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Address *</label>
        <Textarea data-testid="reg-address" className="mt-1.5" value={form.address} onChange={set("address")} required minLength={5} rows={2} placeholder="City, State" />
      </div>
      <div>
        <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">How did you find this competition?</label>
        <Select value={form.referral_source} onValueChange={(v) => setForm((f) => ({ ...f, referral_source: v }))}>
          <SelectTrigger data-testid="reg-source" className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SOURCES.map((s) => <SelectItem key={s} value={s} data-testid={`reg-source-${s}`}>{s.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
        <Checkbox data-testid="reg-terms" checked={form.terms_accepted} onCheckedChange={(v) => setForm((f) => ({ ...f, terms_accepted: !!v }))} />
        <span>I accept the <Link to="/rules" className="text-[#FF9933] font-semibold underline">competition rules</Link>, anti-cheat policy and privacy terms. *</span>
      </label>
      <button type="submit" disabled={busy || dnStatus?.available === false} data-testid="reg-submit-btn"
        className="w-full py-3.5 rounded-lg bg-[#FF9933] text-white font-bold hover:bg-[#E6892E] transition-colors disabled:opacity-50">
        {busy ? "Registering…" : "Complete Registration"}
      </button>
    </motion.form>
  );
}

function Confirmation({ user, onContinue }) {
  const { config } = useApp();
  const comp = config?.competition;
  const shareText = encodeURIComponent(`I just registered for the ${comp?.org} ${comp?.title}! 🇮🇳 Join me: ${window.location.origin}`);
  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.origin);
    toast.success("Link copied!");
  };
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="registration-confirmation"
      className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-xl w-full max-w-xl text-center relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1.5 tricolor-bar" />
      <div className="w-16 h-16 mx-auto rounded-full bg-[#138808]/10 flex items-center justify-center mb-5">
        <PartyPopper className="w-8 h-8 text-[#138808]" />
      </div>
      <h2 className="text-3xl font-black font-heading text-slate-900 tracking-tight">You're Registered! 🇮🇳</h2>
      <p className="text-slate-500 mt-2 text-sm">Welcome aboard, <b className="text-slate-800">{user?.name}</b>. You'll compete as</p>
      <div data-testid="confirmed-display-name" className="mt-2 inline-block px-4 py-1.5 rounded-lg bg-[#FF9933]/10 border border-[#FF9933]/30 text-[#E6892E] font-timer font-bold text-xl">{user?.display_name}</div>
      <div className="grid grid-cols-3 gap-3 mt-7 text-center">
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400">Date</div>
          <div className="font-bold text-sm text-slate-800 mt-1">{comp ? formatIST(comp.start_at) : "15 Aug"}</div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400">Categories</div>
          <div className="font-timer font-bold text-lg text-slate-800">{config?.schedule?.length || 10}</div>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400">Questions</div>
          <div className="font-timer font-bold text-lg text-slate-800">{config?.schedule?.reduce((a, c) => a + (c.question_count || 10), 0) || 100}</div>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-4 flex items-center justify-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#138808]" /> A confirmation email has been sent.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <a data-testid="share-whatsapp" href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg border border-[#138808] text-[#138808] text-sm font-bold hover:bg-[#138808] hover:text-white transition-colors inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> WhatsApp</a>
        <button data-testid="share-copy" onClick={copyLink} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-bold hover:border-slate-500 transition-colors inline-flex items-center gap-2"><Copy className="w-4 h-4" /> Copy Link</button>
        {config?.socials?.discord && <a data-testid="share-discord" href={config.socials.discord} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg border border-[#003366] text-[#003366] text-sm font-bold hover:bg-[#003366] hover:text-white transition-colors">Discord</a>}
      </div>
      <button onClick={onContinue} data-testid="confirmation-dashboard-btn"
        className="mt-7 w-full py-3.5 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors inline-flex items-center justify-center gap-2">
        <BellRing className="w-4 h-4" /> Go to My Dashboard
      </button>
    </motion.div>
  );
}

export default function Auth() {
  const { user, setUser, config } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [justRegistered, setJustRegistered] = useState(null);

  useEffect(() => {
    if (user?.profile_complete && !justRegistered) navigate("/dashboard");
  }, [user, justRegistered, navigate]);

  const onGoogle = async (credential) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/google", { credential });
      localStorage.setItem("lostdc_token", data.token);
      setUser(data.user);
      toast.success("Signed in with Google");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onTestLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/test-login", { name: "Test Player" });
      localStorage.setItem("lostdc_token", data.token);
      setUser(data.user);
      toast.success("Test session started — complete your profile");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] relative flex items-center justify-center px-5 py-16">
      <div className="absolute inset-0 tricolor-glow" />
      <div className="absolute inset-0 chakra-watermark" />
      <div className="relative w-full flex justify-center">
        <AnimatePresence mode="wait">
          {justRegistered ? (
            <Confirmation key="conf" user={justRegistered} onContinue={() => navigate("/dashboard")} />
          ) : user && !user.profile_complete ? (
            <ProfileForm key="profile" onDone={(u) => setJustRegistered(u)} />
          ) : (
            <motion.div key="login" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-xl w-full max-w-md relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 tricolor-bar" />
              <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tight">Join the Competition</h1>
              <p className="text-sm text-slate-500 mt-2">Register with your Google account. Your Gmail stays private — you choose a public display name.</p>
              {!config?.competition?.registration_open && (
                <div data-testid="registration-closed" className="mt-4 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  Registrations are currently closed by the administrator.
                </div>
              )}
              <div className="mt-7 flex flex-col items-center gap-4">
                <GoogleButton onCredential={onGoogle} />
                {config?.test_login_enabled && (
                  <>
                    <div className="flex items-center gap-3 w-full">
                      <div className="h-px flex-1 bg-slate-200" /><span className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase">or</span><div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <button onClick={onTestLogin} disabled={busy} data-testid="test-login-btn"
                      className="w-full py-3 rounded-lg border-2 border-[#003366] text-[#003366] font-bold hover:bg-[#003366] hover:text-white transition-colors disabled:opacity-50">
                      Continue with Test Login
                    </button>
                  </>
                )}
              </div>
              <p className="mt-6 text-xs text-slate-400 text-center">By registering you agree to the <Link to="/rules" className="underline">competition rules</Link> and anti-cheat policy.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
